package com.yendo.mx.ui.restaurant

import androidx.compose.runtime.mutableStateMapOf
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.yendo.mx.models.Producto
import com.yendo.mx.repository.ProductoRepository
import dev.gitlive.firebase.firestore.DocumentSnapshot
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

data class CategoryState(
    val products: List<Producto> = emptyList(),
    val lastSnapshot: DocumentSnapshot? = null,
    val isLoading: Boolean = false,
    val isLastPage: Boolean = false
)

class RestauranteViewModel : ViewModel() {

    // 🚀 ESTADO MULTI-CURSOR: Un estado independiente por cada categoría
    private val _categoryStates = mutableStateMapOf<String, CategoryState>()
    val categoryStates: Map<String, CategoryState> = _categoryStates

    // 🚀 ESTADO PARA MODO RESTAURANTE (CARGA COMPLETA)
    private val _allProducts = MutableStateFlow<List<Producto>>(emptyList())
    val allProducts: StateFlow<List<Producto>> = _allProducts

    // 🚀 ESTADO DE BÚSQUEDA (ALGOLIA)
    private val _searchResults = MutableStateFlow<List<Producto>>(emptyList())
    val searchResults: StateFlow<List<Producto>> = _searchResults

    private val _isSearching = MutableStateFlow(false)
    val isSearching: StateFlow<Boolean> = _isSearching.asStateFlow()

    private var restauranteActualId: String? = null

    /**
     * 🚀 DESCONTAMINADOR ATÓMICO: Limpia los estados cuando el usuario cambia de restaurante.
     */
    fun prepararParaRestaurante(idRestaurante: String) {
        if (restauranteActualId != idRestaurante) {
            println("YENDO_LOG REFACTOR: Preparando ViewModel para nuevo restaurante '$idRestaurante' (Anterior: '$restauranteActualId')")
            restauranteActualId = idRestaurante
            _categoryStates.clear()
            _allProducts.value = emptyList()
            _searchResults.value = emptyList()
        }
    }

    /**
     * Carga todos los productos del restaurante (Modo RESTAURANTE clásico).
     */
    fun cargarMenuCompleto(idRestaurante: String) {
        prepararParaRestaurante(idRestaurante)
        viewModelScope.launch {
            ProductoRepository.obtenerProductosPorRestaurante(idRestaurante)
                .collect { products ->
                    _allProducts.value = products
                }
        }
    }

    /**
     * Carga la primera página de productos para una categoría (Modo COMERCIO).
     */
    fun cargarProductosPorCategoria(idRestaurante: String, categoria: String) {
        prepararParaRestaurante(idRestaurante)
        val claveCompuesta = "${idRestaurante}_${categoria}"
        if (_categoryStates.containsKey(claveCompuesta)) return

        _categoryStates[claveCompuesta] = CategoryState(isLoading = true)

        viewModelScope.launch {
            val result = ProductoRepository.obtenerProductosPaginados(
                idRestaurante = idRestaurante,
                categoria = categoria,
                limit = 20
            )
            _categoryStates[claveCompuesta] = CategoryState(
                products = result.products,
                lastSnapshot = result.lastSnapshot,
                isLoading = false,
                isLastPage = result.products.size < 20
            )
        }
    }

    /**
     * Carga la siguiente página de productos para una categoría (Paginación Infinita).
     */
    fun cargarSiguientePagina(idRestaurante: String, categoria: String) {
        val claveCompuesta = "${idRestaurante}_${categoria}"
        val currentState = _categoryStates[claveCompuesta] ?: return
        if (currentState.isLoading || currentState.isLastPage) return

        _categoryStates[claveCompuesta] = currentState.copy(isLoading = true)

        viewModelScope.launch {
            val result = ProductoRepository.obtenerProductosPaginados(
                idRestaurante = idRestaurante,
                categoria = categoria,
                limit = 20,
                startAfter = currentState.lastSnapshot
            )

            val updatedProducts = currentState.products + result.products
            _categoryStates[claveCompuesta] = currentState.copy(
                products = updatedProducts,
                lastSnapshot = result.lastSnapshot,
                isLoading = false,
                isLastPage = result.products.size < 20
            )
        }
    }

    /**
     * 🚀 CONSULTA SEGURA POR CLAVE COMPUESTA (Evita cruces entre restaurantes)
     */
    fun obtenerEstadoCategoria(idRestaurante: String, categoria: String): CategoryState {
        val claveCompuesta = "${idRestaurante}_${categoria}"
        return _categoryStates[claveCompuesta] ?: CategoryState()
    }

    /**
     * 🚀 VERIFICA SI UNA CATEGORÍA YA FUE CONSULTADA EN FIRESTORE
     */
    fun hasCategoryFetched(idRestaurante: String, categoria: String): Boolean {
        val claveCompuesta = "${idRestaurante}_${categoria}"
        return _categoryStates.containsKey(claveCompuesta)
    }

    /**
     * 🚀 BÚSQUEDA OPTIMIZADA CON ALGOLIA (Anti-Costos Firestore)
     */
    fun buscarProductos(query: String, idRestaurante: String, idZona: String) {
        if (query.isBlank()) {
            _searchResults.value = emptyList()
            _isSearching.value = false
            return
        }

        _isSearching.value = true
        viewModelScope.launch {
            try {
                val results = com.yendo.mx.repository.AlgoliaRepository.buscarProductosEnRestaurante(
                    query = query,
                    idRestaurante = idRestaurante,
                    idZona = idZona
                )
                _searchResults.value = results
            } catch (e: Exception) {
                _searchResults.value = emptyList()
            } finally {
                _isSearching.value = false
            }
        }
    }

    /**
     * 🚀 REINICIAR CARGA: Limpia los estados para forzar un Pull to Refresh real
     */
    fun reiniciarCarga() {
        _categoryStates.clear()
        _allProducts.value = emptyList()
    }
}
